import { fmtErr } from "@/lib/utils";
import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';

const NOTIFICATION_ID_BASE = 9000;
// Fixed IDs for the recurring lifecycle notifications (kept out of the
// 9000-9999 band used by per-scan meal reminders).
const WRAPPED_NOTIFICATION_ID = 8001;
const WINBACK_NOTIFICATION_ID = 8002;
const WINBACK_DELAY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/** True only if notification permission is ALREADY granted. Never prompts. */
async function notificationsGranted(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    const perm = await LocalNotifications.checkPermissions();
    return perm.display === 'granted';
  } catch {
    return false;
  }
}

/**
 * Schedule a post-meal check-in notification.
 * Lunch (scan before 15:00 local) → 1 hour delay
 * Dinner (scan after 18:00 local) → 1.5 hour delay
 * Default → 1.25 hours
 *
 * Wave Q: this no longer REQUESTS permission — that happens once, in
 * context, via the priming sheet (enableNotifications). Here we only
 * check; if the user hasn't opted in we quietly skip.
 */
export async function schedulePostMealNotification(
  restaurantName: string,
  menuId: string,
  scanTimestamp: number
) {
  if (!(await notificationsGranted())) {
    console.log('[mealNotify] Not granted (or not native) — skipping post-meal notification');
    return;
  }

  try {
    const scanHour = new Date(scanTimestamp).getHours();
    let delayMs: number;
    if (scanHour < 15) {
      delayMs = 60 * 60 * 1000; // 1 hour for lunch
    } else if (scanHour >= 18) {
      delayMs = 90 * 60 * 1000; // 1.5 hours for dinner
    } else {
      delayMs = 75 * 60 * 1000; // 1.25 hours default
    }

    const notifyAt = new Date(scanTimestamp + delayMs);
    const notificationId = NOTIFICATION_ID_BASE + (scanTimestamp % 1000);

    await LocalNotifications.schedule({
      notifications: [
        {
          id: notificationId,
          title: `How was ${restaurantName}? 🍽️`,
          body: 'Rate your dishes & share the slaps with friends',
          schedule: { at: notifyAt },
          extra: { menuId, action: 'rate-dishes' },
          smallIcon: 'ic_stat_icon_config_sample',
          largeIcon: 'splash',
        },
      ],
    });

    console.log(`[mealNotify] Scheduled for ${notifyAt.toISOString()} (${Math.round(delayMs / 60000)} min delay)`);

    // Store in localStorage so we don't re-schedule
    const pending = JSON.parse(localStorage.getItem('yumz-pending-notifications') || '[]');
    pending.push({ menuId, restaurantName, scheduledAt: notifyAt.toISOString(), notificationId });
    localStorage.setItem('yumz-pending-notifications', JSON.stringify(pending));

  } catch (err) {
    console.error('[mealNotify] Failed to schedule:', fmtErr(err));
  }
}

/**
 * Wave Q — recurring lifecycle notifications (local, no server needed):
 *   • Weekly Wrapped — every Monday 09:00, "your wrapped is ready" → /stats
 *   • Inactivity win-back — fires ~7 days out; we cancel + reschedule it on
 *     every app open, so it only ever fires for a genuinely lapsed user.
 * Both are idempotent (fixed IDs, cancel-before-schedule). No-ops unless
 * permission is already granted, so it's safe to call on every launch.
 */
export async function scheduleLifecycleNotifications() {
  if (!(await notificationsGranted())) return;
  try {
    // Reset both so repeated launches don't pile up duplicates.
    await LocalNotifications.cancel({
      notifications: [{ id: WRAPPED_NOTIFICATION_ID }, { id: WINBACK_NOTIFICATION_ID }],
    });

    await LocalNotifications.schedule({
      notifications: [
        {
          id: WRAPPED_NOTIFICATION_ID,
          title: 'your yumz wrapped is ready 🎉',
          body: 'see your week — your top dishes, spots & flavor profile',
          schedule: { on: { weekday: 2, hour: 9, minute: 0 }, repeats: true },
          extra: { action: 'wrapped' },
          smallIcon: 'ic_stat_icon_config_sample',
          largeIcon: 'splash',
        },
        {
          id: WINBACK_NOTIFICATION_ID,
          title: "what's for dinner? 👀",
          body: 'point your camera at any menu and watch it come to life',
          schedule: { at: new Date(Date.now() + WINBACK_DELAY_MS) },
          extra: { action: 'winback' },
          smallIcon: 'ic_stat_icon_config_sample',
          largeIcon: 'splash',
        },
      ],
    });
    console.log('[mealNotify] Lifecycle notifications (wrapped weekly + 7d win-back) scheduled');
  } catch (err) {
    console.error('[mealNotify] Lifecycle schedule failed:', fmtErr(err));
  }
}

let tapRoutingWired = false;
/**
 * Route taps on local notifications to the right screen. Dispatches a
 * `yumz:notif-nav` event with the target path; a router-aware component
 * (NotificationManager) listens and navigates. Idempotent.
 */
export function setupLocalNotificationTapRouting() {
  if (!Capacitor.isNativePlatform() || tapRoutingWired) return;
  tapRoutingWired = true;
  try {
    LocalNotifications.addListener('localNotificationActionPerformed', (event) => {
      const extra = (event.notification?.extra || {}) as { action?: string; menuId?: string };
      let path = '/';
      if (extra.action === 'rate-dishes') path = extra.menuId ? `/menu/${extra.menuId}` : '/my-dishes';
      else if (extra.action === 'wrapped') path = '/stats';
      else if (extra.action === 'winback') path = '/';
      document.dispatchEvent(new CustomEvent('yumz:notif-nav', { detail: { path } }));
    });
  } catch (err) {
    console.error('[mealNotify] tap routing setup failed:', fmtErr(err));
  }
}

/**
 * Cancel a pending notification for a menu
 */
export async function cancelMealNotification(menuId: string) {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const pending = JSON.parse(localStorage.getItem('yumz-pending-notifications') || '[]');
    const match = pending.find((p: any) => p.menuId === menuId);
    if (match) {
      await LocalNotifications.cancel({ notifications: [{ id: match.notificationId }] });
      const updated = pending.filter((p: any) => p.menuId !== menuId);
      localStorage.setItem('yumz-pending-notifications', JSON.stringify(updated));
    }
  } catch (err) {
    console.error('[mealNotify] Cancel failed:', fmtErr(err));
  }
}
